import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CarOutlined, ClockCircleOutlined, EnvironmentOutlined, LinkOutlined, PhoneOutlined, RightOutlined, UserOutlined, } from "@ant-design/icons";
import { Button, message } from "antd";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import React from "react";
import { useNavigate } from "react-router-dom";
import { OrderStatusTag } from "@/components/OrderForm/OrderStatusTag";
import { getRootStore } from "@/stores/RootStore";
import { findOptimalBase } from "@/utils/routeOptimization";
import { onError } from "@/utils/onError";
import styles from "@/styles/Dashboard/OrderCardCompact.module.less";
// Окно «скоро»: визит ближе 12 часов → янтарная лента (решение пользователя)
const SOON_THRESHOLD_MS = 12 * 60 * 60 * 1000;
/**
 * Срочность заказа по времени визита (scheduledDate).
 */
const getUrgency = (scheduledDate) => {
    const diffMs = scheduledDate.diff(dayjs());
    if (diffMs <= 0)
        return "overdue";
    if (diffMs < SOON_THRESHOLD_MS)
        return "soon";
    return "planned";
};
/**
 * Сокращённая длительность: дни → "N дн.", иначе "H ч M мин", иначе "M мин".
 */
const formatDuration = (ms) => {
    const totalMin = Math.floor(ms / 60000);
    const days = Math.floor(totalMin / (60 * 24));
    const hours = Math.floor((totalMin % (60 * 24)) / 60);
    const minutes = totalMin % 60;
    if (days > 0)
        return `${days} дн.`;
    if (hours > 0)
        return minutes > 0 ? `${hours} ч ${minutes} мин` : `${hours} ч`;
    return `${minutes} мин`;
};
/**
 * Текст «остатка» до/после дедлайна.
 */
const getRemainingText = (scheduledDate) => {
    const diffMs = scheduledDate.diff(dayjs());
    if (diffMs <= 0)
        return `Просрочено на ${formatDuration(-diffMs)}`;
    return `Осталось ${formatDuration(diffMs)}`;
};
/**
 * Очистка части имени от плейсхолдеров: "-", "—", "–", "." и т.п. → пусто.
 * В тестовых/боевых данных фамилия нередко записана как "-".
 */
const cleanNamePart = (s) => {
    const t = (s ?? "").trim();
    if (!t || /^[-—–.\s]+$/.test(t))
        return "";
    return t;
};
/**
 * Инициалы клиента (для аватара). Пусто, если имя не указано.
 */
const getInitials = (firstName, lastName) => {
    const a = cleanNamePart(firstName).charAt(0);
    const b = cleanNamePart(lastName).charAt(0);
    return `${a}${b}`.toUpperCase();
};
/**
 * Форматирование телефона: display для UI, tel для ссылки tel:.
 * Российские номера 7/8 + 10 цифр → "+7 999 123-45-67".
 */
const formatPhone = (raw) => {
    const digits = raw.replace(/\D/g, "");
    // 11 цифр с кодом страны 7/8 или 10 цифр без кода
    let local = null;
    if (digits.length === 11 && (digits[0] === "7" || digits[0] === "8")) {
        local = digits.slice(1);
    }
    else if (digits.length === 10) {
        local = digits;
    }
    if (local) {
        const display = `+7 ${local.slice(0, 3)} ${local.slice(3, 6)}-${local.slice(6, 8)}-${local.slice(8, 10)}`;
        return { display, tel: `+7${local}` };
    }
    // Иной формат — показываем как есть, в tel: только цифры (+ префикс если был)
    return { display: raw, tel: raw.trim().startsWith("+") ? `+${digits}` : digits };
};
const OrderCardCompact = observer(({ order, isSelected, isUpdated, onClick, }) => {
    const navigate = useNavigate();
    const { basesStore } = getRootStore();
    const [isCalculating, setIsCalculating] = React.useState(false);
    // Дата/время визита + срочность
    const scheduledDate = order.scheduledDate ? dayjs(order.scheduledDate) : null;
    const formattedDateTime = scheduledDate
        ? scheduledDate.format("DD.MM.YYYY HH:mm")
        : "Не указано";
    const urgency = scheduledDate ? getUrgency(scheduledDate) : "planned";
    const remainingText = scheduledDate ? getRemainingText(scheduledDate) : "";
    // Адрес
    const rawAddress = order.addressInfo?.getAddress();
    const hasCoordinates = order.addressInfo?.getLatitude() && order.addressInfo?.getLongitude();
    const address = rawAddress || (hasCoordinates ? "По координатам" : "Адрес не указан");
    // Авто клиента (вторичная строка)
    const vehicleInfo = order.vehicleInfo;
    const vehicleText = vehicleInfo
        ? [
            vehicleInfo.brand || vehicleInfo.manufacturer,
            vehicleInfo.model,
            vehicleInfo.batteryCapacity && `${vehicleInfo.batteryCapacity} кВт·ч`,
            vehicleInfo.connectorType || vehicleInfo.dcPort,
        ]
            .filter(Boolean)
            .join(" · ")
        : null;
    // Клиент
    const clientInfo = order.clientInfo;
    const clientName = [cleanNamePart(clientInfo?.firstName), cleanNamePart(clientInfo?.lastName)]
        .filter(Boolean)
        .join(" ");
    const phone = clientInfo?.phone?.trim() || "";
    const hasClient = Boolean(clientName || phone);
    const initials = getInitials(clientInfo?.firstName, clientInfo?.lastName);
    const formattedPhone = phone ? formatPhone(phone) : null;
    // Переход в заказ по плашке №заказа (gasим всплытие, чтобы не сработала «карта»)
    const handlePillClick = (e) => {
        e.stopPropagation();
        // cmd/ctrl-клик — пусть браузер откроет в новой вкладке
        if (e.metaKey || e.ctrlKey)
            return;
        e.preventDefault();
        navigate(`/orders/${order.id}`);
    };
    // Кнопка «В маршрут» — поведение без изменений
    const handleCreateRoute = async (e) => {
        e.stopPropagation(); // Предотвратить выделение карточки
        setIsCalculating(true);
        try {
            // Загрузить базы если ещё не загружены
            if (basesStore.dataTable.data.length === 0) {
                await basesStore.dataTable.loadData();
            }
            const bases = basesStore.dataTable.data;
            if (bases.length === 0) {
                message.error("Нет доступных баз для создания маршрута");
                return;
            }
            // Используем утилиту для поиска оптимальной базы.
            const result = await findOptimalBase(order, bases);
            // Переход на форму создания маршрута с предрасчитанными точками
            navigate("/routes/new", {
                state: {
                    quickOrder: {
                        baseId: result.base.id,
                        orderId: order.id,
                        plannedStartTime: result.startTime.toISOString(),
                        precalculatedPoints: result.precalculatedPoints,
                    },
                },
            });
        }
        catch (err) {
            onError(err instanceof Error ? err.message : "Ошибка расчёта маршрута", "Ошибка");
        }
        finally {
            setIsCalculating(false);
        }
    };
    // Keyboard navigation — Enter/Space по карточке = локация на карте
    const handleKeyDown = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
        }
    };
    const cardClassName = [
        styles.card,
        urgency === "overdue" ? styles.overdue : urgency === "soon" ? styles.soon : "",
        isSelected ? styles.selected : "",
    ]
        .filter(Boolean)
        .join(" ");
    const ariaLabel = `Заказ ${order.orderNumber}, ${formattedDateTime}, ${address}`;
    return (_jsxs("article", { className: cardClassName, onClick: onClick, onKeyDown: handleKeyDown, tabIndex: 0, role: "button", "aria-pressed": isSelected, "aria-label": ariaLabel, children: [_jsx("span", { className: styles.rail, "aria-hidden": "true" }), isUpdated && (_jsx("div", { className: styles.updateBadge, children: _jsx("span", { className: styles.updateDot }) })), _jsxs("div", { className: styles.body, children: [_jsxs("div", { className: styles.top, children: [_jsxs("a", { className: styles.pill, href: `/orders/${order.id}`, onClick: handlePillClick, children: [_jsx(LinkOutlined, { className: styles.pillIcon }), "#", order.orderNumber, _jsx(RightOutlined, { className: styles.pillChev })] }), _jsx("span", { className: styles.status, children: _jsx(OrderStatusTag, { status: order.status }) }), _jsx("span", { className: styles.hint, children: "\u2190 \u0442\u0430\u043F \u2192 \u0437\u0430\u043A\u0430\u0437" })] }), _jsxs("div", { className: styles.when, children: [_jsx(ClockCircleOutlined, { className: styles.whenIcon }), _jsxs("div", { children: [_jsx("div", { className: styles.whenDatetime, children: formattedDateTime }), remainingText && _jsx("div", { className: styles.whenLeft, children: remainingText })] })] }), _jsxs("p", { className: styles.row, children: [_jsx(EnvironmentOutlined, { className: styles.rowIcon }), _jsx("span", { className: styles.rowText, children: address })] }), vehicleText && (_jsxs("p", { className: `${styles.row} ${styles.rowMuted}`, children: [_jsx(CarOutlined, { className: styles.rowIcon }), _jsx("span", { className: styles.rowText, children: vehicleText })] })), _jsxs("div", { className: styles.client, children: [_jsxs("div", { className: styles.person, children: [hasClient && initials ? (_jsx("span", { className: styles.avatar, children: initials })) : (_jsx("span", { className: styles.avatar, children: _jsx(UserOutlined, { className: styles.avatarIcon }) })), hasClient ? (_jsxs("span", { children: [_jsx("span", { className: styles.clientLabel, children: "\u041A\u043B\u0438\u0435\u043D\u0442" }), clientName ? (_jsx("span", { className: styles.clientName, children: clientName })) : (_jsx("span", { className: styles.clientNameMissing, children: "\u0418\u043C\u044F \u043D\u0435 \u0443\u043A\u0430\u0437\u0430\u043D\u043E" }))] })) : (_jsx("span", { className: styles.clientEmpty, children: "\u041A\u043B\u0438\u0435\u043D\u0442 \u043D\u0435 \u043D\u0430\u0437\u043D\u0430\u0447\u0435\u043D" }))] }), formattedPhone && (_jsxs("a", { className: styles.callBtn, href: `tel:${formattedPhone.tel}`, onClick: (e) => e.stopPropagation(), children: [_jsx(PhoneOutlined, { className: styles.callIcon }), formattedPhone.display] }))] }), _jsx(Button, { type: "primary", icon: _jsx(RightOutlined, {}), loading: isCalculating, onClick: handleCreateRoute, className: styles.routeBtn, children: "\u0412 \u043C\u0430\u0440\u0448\u0440\u0443\u0442" })] })] }));
});
OrderCardCompact.displayName = "OrderCardCompact";
export default OrderCardCompact;
